/**
 * A saved quicklink. `icon` is a lucide icon NAME (e.g. "GitBranch"); `color`
 * is an accent-hue name (see iconAccents.ts) for the icon chip, optional —
 * when absent the chip falls back to a stable hue derived from the id.
 */
export interface Quicklink {
  id: string;
  title: string;
  url: string;
  icon: string;
  color?: string;
}

/** StorageApi bucket + key holding the per-user list (DB-backed per user). */
export const QUICKLINKS_BUCKET = 'quicklinks';
export const QUICKLINKS_KEY = 'items';

/** Fallback icon when none is chosen / a stored name no longer resolves. */
export const DEFAULT_ICON = 'Link';

/** Build a new quicklink with a stable local id and a default icon. */
export function newQuicklink(input: {
  title: string;
  url: string;
  icon?: string;
  color?: string;
}): Quicklink {
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title: input.title,
    url: input.url,
    icon: input.icon || DEFAULT_ICON,
    ...(input.color ? { color: input.color } : {}),
  };
}

export function addLink(list: Quicklink[], link: Quicklink): Quicklink[] {
  return [...list, link];
}

export function removeLink(list: Quicklink[], id: string): Quicklink[] {
  return list.filter(l => l.id !== id);
}

export function renameLink(
  list: Quicklink[],
  id: string,
  title: string,
): Quicklink[] {
  return list.map(l => (l.id === id ? { ...l, title } : l));
}

export function setLinkIcon(
  list: Quicklink[],
  id: string,
  icon: string,
): Quicklink[] {
  return list.map(l => (l.id === id ? { ...l, icon } : l));
}

export function setLinkColor(
  list: Quicklink[],
  id: string,
  color: string,
): Quicklink[] {
  return list.map(l => (l.id === id ? { ...l, color } : l));
}

/** Edit a link's title and url together (one write). */
export function editLink(
  list: Quicklink[],
  id: string,
  patch: { title: string; url: string },
): Quicklink[] {
  return list.map(l =>
    l.id === id ? { ...l, title: patch.title, url: patch.url } : l,
  );
}

export function moveLink(
  list: Quicklink[],
  id: string,
  dir: 'up' | 'down',
): Quicklink[] {
  const i = list.findIndex(l => l.id === id);
  if (i === -1) return list;
  const j = dir === 'up' ? i - 1 : i + 1;
  if (j < 0 || j >= list.length) return list;
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
}
